import React, { createContext, useState, useContext, ReactNode, useCallback, useMemo, useEffect, useRef } from 'react';
import { REDIRECT_DELAY_MS } from '../config';

interface IRedirectContext {
    isRedirecting: boolean;
    redirectSite: string;
    redirectUrl: string;
    isPopupBlocked: boolean;
    redirect: (siteName: string, url: string) => void;
    cancelRedirect: () => void;
}

const RedirectContext = createContext<IRedirectContext | undefined>(undefined);

export const RedirectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [redirectSite, setRedirectSite] = useState('');
    const [redirectUrl, setRedirectUrl] = useState('');
    const [isPopupBlocked, setIsPopupBlocked] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                setIsRedirecting(false);
                setRedirectSite('');
                setRedirectUrl('');
                setIsPopupBlocked(false);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            }
        };

        window.addEventListener('pageshow', handlePageShow);
        return () => {
            window.removeEventListener('pageshow', handlePageShow);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const redirect = useCallback((siteName: string, url: string) => {
        setRedirectSite(siteName);
        setRedirectUrl(url);
        setIsRedirecting(true);
        setIsPopupBlocked(false);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            const redirectUrlTarget = url;

            let newWindow: Window | null = null;
            try {
                newWindow = typeof window !== 'undefined' ? window.open(redirectUrlTarget, '_blank', 'noopener,noreferrer') : null;
            } catch (e) {
                console.error('Failed to open window:', e);
            }

            const wasBlocked = !newWindow || newWindow.closed || typeof newWindow.closed === 'undefined';

            if (wasBlocked) {
                setIsPopupBlocked(true);
            } else {
                setTimeout(() => {
                    setIsRedirecting(false);
                    setRedirectSite('');
                    setRedirectUrl('');
                }, 500);
            }
        }, REDIRECT_DELAY_MS);
    }, []);

    const cancelRedirect = useCallback(() => {
        setIsRedirecting(false);
        setRedirectSite('');
        setRedirectUrl('');
        setIsPopupBlocked(false);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const value = useMemo(() => ({
        isRedirecting,
        redirectSite,
        redirectUrl,
        isPopupBlocked,
        redirect,
        cancelRedirect,
    }), [isRedirecting, redirectSite, redirectUrl, isPopupBlocked, redirect, cancelRedirect]);

    return (
        <RedirectContext.Provider value={value}>
            {children}
        </RedirectContext.Provider>
    );
};

export const useRedirect = (): IRedirectContext => {
    const context = useContext(RedirectContext);
    if (context === undefined) {
        throw new Error('useRedirect must be used within a RedirectProvider');
    }
    return context;
};